import { getUserPreferredFacilities, UserPreferredFacility } from "@services/payment";
import { getUserFacilities } from "@services/facilityManagement";
import { getCurrentUserId } from "@utils/userUtils";

/** Vertical stamped on preferred facility details; legacy rows default to healthcare. */
export function preferredFacilityVertical(
  facility: UserPreferredFacility,
): string {
  const raw = facility.facility_details?.vertical;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim().toLowerCase();
  }
  return "healthcare";
}

/** Keep preferred facilities that match the landing/login vertical (e.g. aviation). */
export function filterPreferredFacilitiesByVertical(
  facilities: UserPreferredFacility[],
  vertical: string | null | undefined,
): UserPreferredFacility[] {
  if (!vertical || !String(vertical).trim()) return facilities;
  const want = String(vertical).trim().toLowerCase();
  return facilities.filter((f) => preferredFacilityVertical(f) === want);
}

/** Preferred rows used for Dashboard pricing / verification redirects. */
export function preferredFacilitiesForVerticalRouting(
  facilities: UserPreferredFacility[],
  vertical: string | null | undefined,
): UserPreferredFacility[] {
  return filterPreferredFacilitiesByVertical(
    facilities.filter((f) => !f.is_delete),
    vertical,
  );
}

/**
 * After card association, FacilityValidation navigates to verificationPending and
 * sets current_verification_facility before preferred-facilities refresh catches up.
 * Case 1 must not wipe that to /facilities/home (CI 32731 Idaho Mackay stuck
 * Verification pending on home — never Select Plan).
 */
export function shouldPreserveVerificationPendingOverHome(input: {
  currentView?: string | null;
  skipHomeRedirect?: boolean | null;
  hasTrackedPendingFacility: boolean;
}): boolean {
  if (input.skipHomeRedirect) return true;
  if (input.hasTrackedPendingFacility) return true;
  const view = String(input.currentView ?? "");
  return view === "verificationPending" || view === "verificationFailed";
}

function storePreferredFacilityForPricing(facility: UserPreferredFacility): void {
  const facilityDetails = facility.facility_details || null;
  const facilityTypeId = (facilityDetails as { facility_type_id?: number } | null)
    ?.facility_type_id ?? null;
  sessionStorage.setItem(
    "preferred_facility_for_pricing",
    JSON.stringify({
      id: facility.id,
      user_id: facility.user_id,
      is_verified: facility.is_verified,
      is_active: facility.is_active,
      facility_name:
        facility.facility_name ||
        (facilityDetails as { facility_name?: string } | null)?.facility_name ||
        "",
      facility_type:
        facility.facility_type ||
        (facilityDetails as { facility_type?: string } | null)?.facility_type ||
        "",
      facility_type_id: facilityTypeId,
      discount_coupon_name: facility.discount_coupon_name ?? null,
      discount_end_date: facility.discount_end_date ?? null,
      facility_details: facilityDetails,
      vertical: preferredFacilityVertical(facility),
    }),
  );
}

/**
 * After sign-in: if user has exactly one preferred facility (optionally scoped to
 * the landing vertical from /aviation, /healthcare, …), it's verified, and payment
 * is pending (no user facilities / no paid subscription), store it for pricing and
 * return the facility. Returns null otherwise.
 */
export async function tryGetSinglePaymentPendingFacilityForPricing(
  vertical?: string | null,
): Promise<UserPreferredFacility | null> {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    const userIdStr = getCurrentUserId();
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    if (!userId || isNaN(userId)) return null;

    const [preferredRes, userFacilitiesRes] = await Promise.all([
      getUserPreferredFacilities(1, 50, userId, true),
      getUserFacilities(userId),
    ]);

    const fromResult = preferredRes?.result?.data;
    const fromTopLevel = (preferredRes as { data?: unknown })?.data;
    const facilities: UserPreferredFacility[] = Array.isArray(fromResult)
      ? fromResult
      : Array.isArray(fromTopLevel)
        ? (fromTopLevel as UserPreferredFacility[])
        : [];
    const preferred = filterPreferredFacilitiesByVertical(
      facilities.filter((f) => !f.is_delete),
      vertical,
    );
    const userFacs = userFacilitiesRes?.facilities ?? [];

    if (preferred.length !== 1 || userFacs.length > 0) return null;

    const only = preferred[0];
    const isVerified = [
      only.is_verified === true,
      String(only.status || "").toLowerCase() === "verified",
    ].some(Boolean);
    if (!isVerified) return null;

    storePreferredFacilityForPricing(only);
    return only;
  } catch {
    return null;
  }
}

/**
 * If the user has an access token and a verified user preferred facility
 * (optionally scoped to a landing vertical), store it for pricing and return
 * the facility so the caller can redirect to pricing.
 * Returns null if no verified facility or on error.
 */
export async function tryGetVerifiedPreferredFacilityForPricing(
  vertical?: string | null,
): Promise<UserPreferredFacility | null> {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    const res = await getUserPreferredFacilities(1, 50);

    const fromResult = res?.result?.data;
    const fromTopLevel = (res as { data?: unknown })?.data;
    const facilities: UserPreferredFacility[] = Array.isArray(fromResult)
      ? fromResult
      : Array.isArray(fromTopLevel)
        ? (fromTopLevel as UserPreferredFacility[])
        : [];
    if (facilities.length === 0) return null;
    const active = filterPreferredFacilitiesByVertical(
      facilities.filter((f) => !f.is_delete),
      vertical,
    );
    const verified = active.find(
      (f) =>
        [
          f.is_verified === true,
          String(f.status || "").toLowerCase() === "verified",
        ].some(Boolean) &&
        typeof f.id === "number" &&
        f.id > 0,
    );
    if (!verified) return null;
    storePreferredFacilityForPricing(verified);
    return verified;
  } catch {
    return null;
  }
}
